import React from 'react';
import { CheckCircle2Icon, ClockIcon, TrendingUpIcon } from 'lucide-react';
import { ProgressBar } from '../ui/ProgressBar';
interface WelcomeBannerProps {
  firstName: string;
  dateLabel: string;
  completionPercent: number;
  goalsCompleted: number;
  goalsTotal: number;
  lastSyncedLabel: string;
}
type Status = 'complete' | 'on-track' | 'behind';
function resolveStatus(percent: number): Status {
  if (percent >= 100) return 'complete';
  if (percent >= 65) return 'on-track';
  return 'behind';
}
const statusConfig: Record<Status, {
  label: string;
  classes: string;
  icon: typeof TrendingUpIcon;
  tone: 'success' | 'teal' | 'warn';
}> = {
  complete: {
    label: 'Perfect Day Complete',
    classes: 'bg-success-50 text-success-700 ring-success-200',
    icon: CheckCircle2Icon,
    tone: 'success'
  },
  'on-track': {
    label: 'On Track',
    classes: 'bg-teal-50 text-teal-700 ring-teal-200',
    icon: TrendingUpIcon,
    tone: 'teal'
  },
  behind: {
    label: 'Behind Pace',
    classes: 'bg-warn-50 text-warn-700 ring-warn-200',
    icon: ClockIcon,
    tone: 'warn'
  }
};
export function WelcomeBanner({
  firstName,
  dateLabel,
  completionPercent,
  goalsCompleted,
  goalsTotal,
  lastSyncedLabel
}: WelcomeBannerProps) {
  const status = resolveStatus(completionPercent);
  const {
    label,
    classes,
    icon: StatusIcon,
    tone
  } = statusConfig[status];
  return <section aria-labelledby="perfect-day-welcome" className="rounded-2xl border border-navy-100 bg-white p-5 shadow-card sm:p-6" data-id="element-173" data-mp="20ufb2xkf2jp2">
      <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between" data-id="element-174" data-mp="1vx5g72e9dftu">
        <div className="min-w-0" data-id="element-175" data-mp="29w52w4i7nknd">
          <p className="text-xs font-semibold uppercase tracking-wide text-brand-600" data-id="element-176" data-mp="29t0sgpe5jso9">
            {dateLabel}
          </p>
          <h1 id="perfect-day-welcome" className="mt-1 text-2xl font-bold tracking-tight text-navy-900 sm:text-3xl" data-id="element-177" data-mp="3la8z7hc9tkvl">
            Good morning, {firstName}
          </h1>
          <p className="mt-2 max-w-xl text-sm leading-relaxed text-navy-600 sm:text-base" data-id="element-178" data-mp="2dyjb6eydmvyt">
            Your Perfect Day is ready. Start with the highest-impact actions
            first.
          </p>
          <p className="mt-2 text-xs text-navy-400" data-id="element-179" data-mp="2nvo0uorfl9kd">{lastSyncedLabel}</p>
        </div>

        <div className="w-full max-w-md lg:w-80 lg:shrink-0" data-id="element-180" data-mp="2rxiwpdqleuqr">
          <div className="flex items-end justify-between gap-3" data-id="element-181" data-mp="2rwzoioqqvkpx">
            <div data-id="element-182" data-mp="1v8l1juexkut2">
              <p className="text-sm font-medium text-navy-600" data-id="element-183" data-mp="1v938bkz7yrf8">
                Daily completion
              </p>
              <p className="text-3xl font-bold leading-none text-navy-900" data-id="element-184" data-mp="204iju69r8b8h">
                {completionPercent}%
              </p>
            </div>
            <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ring-1 ${classes}`} data-id="element-185" data-mp="28z6nxt3lfmvp">
              <StatusIcon className="h-4 w-4" aria-hidden="true" data-id="element-186" data-mp="1cf7q38qtnh3u" />
              {label}
            </span>
          </div>
          <ProgressBar className="mt-3" size="lg" tone={tone} value={completionPercent} label={`Daily completion ${completionPercent} percent`} data-id="element-187" data-mp="18yuqqhtsi674" />
          <p className="mt-2 text-xs text-navy-500" data-id="element-188" data-mp="zxn795vxn9x3">
            {goalsCompleted} of {goalsTotal} daily goals complete · target 85%
            by 5:00 PM
          </p>
        </div>
      </div>
    </section>;
}